import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { createErrorToast, createSuccessToast } from '../../../redux/modules/app';
import { hideModal } from '../../../redux/modules/modal';
import { useImportAction, useImportRollbackPreview } from '../../../services/import-service';

import { formatDateTime, humanize } from './util';

/**
 * Shows what a rollback would remove and why it might be blocked before the
 * operator confirms; the API applies the same rules on POST.
 */
const RollbackImportModal = ({ importId }) => {
  const dispatch = useDispatch();
  const { data: preview, isLoading, isError, error } = useImportRollbackPreview(importId);
  const { mutateAsync: act, isLoading: rollingBack } = useImportAction(importId);

  const handleConfirm = async () => {
    try {
      await act('rollback');
      dispatch(createSuccessToast({ title: 'Rollback started', description: 'Everything the import created is being removed.' }));
      dispatch(hideModal());
    } catch (err) {
      dispatch(createErrorToast({ title: 'Rollback refused', description: err?.message, error: err }));
    }
  };

  return (
    <Box>
      {isLoading && <Text>Checking what would be removed…</Text>}

      {isError && (
        <Alert borderRadius="md" status="error">
          <AlertIcon />
          <AlertDescription>{error?.message}</AlertDescription>
        </Alert>
      )}

      {preview && (
        <>
          <SimpleGrid columns={4} mb={4} spacing={4}>
            {Object.entries(preview.entities ?? {}).map(([type, count]) => (
              <Stat key={type}>
                <StatLabel textTransform="capitalize">{humanize(type)}</StatLabel>
                <StatNumber fontSize="xl">{count}</StatNumber>
              </Stat>
            ))}
          </SimpleGrid>

          <Text mb={2} textStyle="bodySm">
            {`Only what this import created is removed; matched insureds, projects and files in storage stay. Deadline: ${formatDateTime(preview.deadline)}.`}
          </Text>

          {preview.modifiedCount > 0 && (
            <Alert borderRadius="md" mb={3} status="warning">
              <AlertIcon />
              <AlertDescription>
                {`${preview.modifiedCount} imported entity(ies) were changed after the import finished: `}
                {(preview.modified ?? []).slice(0, 5).map(entry => `${humanize(entry.type)} #${entry.id}`).join(', ')}
              </AlertDescription>
            </Alert>
          )}

          {(preview.blockers ?? []).map(blocker => (
            <Alert borderRadius="md" key={blocker} mb={2} status="error">
              <AlertIcon />
              <AlertDescription>{blocker}</AlertDescription>
            </Alert>
          ))}

          <HStack justifyContent="center" mt={4} spacing={4}>
            <Button variant="secondary" onClick={() => dispatch(hideModal())}>Cancel</Button>
            <Button data-testid="confirm-rollback" isDisabled={!preview.canRollback} isLoading={rollingBack} onClick={handleConfirm}>
              Roll back the import
            </Button>
          </HStack>
        </>
      )}
    </Box>
  );
};

RollbackImportModal.propTypes = { importId: PropTypes.number.isRequired };

export default RollbackImportModal;
